import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { resolveCurrentSender } from '../lib/messagesCore';
import { Device, Call } from '@twilio/voice-sdk';
import { useSupabase } from './SupabaseContext';
import { useBusiness } from './BusinessContext';
import { toast } from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { Capacitor } from '@capacitor/core';
import { CapacitorTwilioVoice } from 'capacitor-twilio-voice';
import { recordPhoneCallOpportunityActivity } from '../lib/crm/recordPhoneCallActivity';
import { ensureOutboundCallHistory } from '../lib/crm/ensureOutboundCallHistory';
import { registerAssistantSurface } from '../lib/assistant/surface';

interface CallInfo {
  id: string;
  from: string;
  to: string;
  status: 'ringing' | 'in-progress' | 'completed' | 'failed' | 'busy' | 'no-answer' | 'canceled';
  direction: 'inbound' | 'outbound';
  businessId: string;
  createdAt: string;
  communication_id?: string;
  first_name?: string;
  last_name?: string;
  customer_phone?: string;
  customer_email?: string;
}

// Active call state for Twilio Voice SDK
interface ActiveCallState {
  call: Call | null;
  phoneNumber: string;
  customerInfo: { id?: string; first_name?: string; last_name?: string } | null;
  callStatus: 'idle' | 'connecting' | 'ringing' | 'connected';
  isMuted: boolean;
  isSpeakerOn: boolean;
  callStartTime: Date | null;
  isMinimized: boolean;
}

interface CallContextType {
  // Legacy call info (from database)
  currentCall: CallInfo | null;
  callHistory: CallInfo[];
  answerCall: () => void;
  rejectCall: () => void;
  clearCurrentCall: () => void;
  setCurrentCall: (call: CallInfo | null) => void;
  
  // New active call state (Twilio Voice SDK)
  activeCallState: ActiveCallState;
  device: Device | null;
  deviceStatus: 'initializing' | 'ready' | 'error' | 'not_configured';
  errorMessage: string;
  
  // Methods for active calls
  startCall: (
    phoneNumber: string,
    customerInfo?: { id?: string; first_name?: string; last_name?: string },
  ) => Promise<void>;
  endCall: () => void;
  toggleMute: () => void;
  toggleSpeaker: () => void;
  sendDTMF: (digit: string) => void;
  minimizeCall: () => void;
  maximizeCall: () => void;
  
  // Device initialization
  initializeDevice: () => Promise<void>;
}

const CallContext = createContext<CallContextType | undefined>(undefined);

export function CallProvider({ children }: { children: React.ReactNode }) {
  const supabaseClient = useSupabase();
  const { selectedBusiness } = useBusiness();
  const [currentCall, setCurrentCall] = useState<CallInfo | null>(null);
  const [callHistory, setCallHistory] = useState<CallInfo[]>([]);
  
  // Twilio Device and active call state
  const deviceRef = useRef<Device | null>(null);
  const isNativeIosCallRef = useRef(false);
  const nativeCallListenerRef = useRef<{ remove: () => Promise<void> } | null>(null);
  const [deviceStatus, setDeviceStatus] = useState<'initializing' | 'ready' | 'error' | 'not_configured'>('initializing');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [activeCallState, setActiveCallState] = useState<ActiveCallState>({
    call: null,
    phoneNumber: '',
    customerInfo: null,
    callStatus: 'idle',
    isMuted: false,
    isSpeakerOn: false,
    callStartTime: null,
    isMinimized: false
  });

  // Format phone number for calling (adds country code)
  const formatPhoneNumberForCalling = (number: string) => {
    const digits = number.replace(/\D/g, '');
    if (digits.length === 10) {
      return `+1${digits}`;
    }
    if (digits.length > 10 && !number.startsWith('+')) {
      return `+${digits}`;
    }
    return number;
  };

  // Format phone number for display
  const formatPhoneNumberForDisplay = (phoneNumber: string) => {
    // Basic US phone number formatting
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Remove country code for display
    const nationalNumber = cleaned.startsWith('1') ? cleaned.substring(1) : cleaned;
    
    if (nationalNumber.length === 10) {
      return `(${nationalNumber.substring(0, 3)}) ${nationalNumber.substring(3, 6)}-${nationalNumber.substring(6, 10)}`;
    }
    
    return phoneNumber;
  };

  // On native iOS we use Twilio Voice iOS SDK via Capacitor plugin (no JS Device)
  const isNativeIos = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

  // Initialize Twilio Device (or mark ready on native iOS)
  const initializeDevice = async () => {
    try {
      if (!selectedBusiness?.id) {
        setErrorMessage('Business ID not found');
        setDeviceStatus('error');
        return;
      }

      const biz = selectedBusiness as any;
      if (!biz.twilio_enabled || !biz.twilio_account_sid || !biz.twilio_auth_token || !biz.VITE_TWILIO_PHONE_NUMBER) {
        setDeviceStatus('not_configured');
        setErrorMessage('');
        return;
      }

      if (isNativeIos) {
        setDeviceStatus('ready');
        setErrorMessage('');
        return;
      }

      // Destroy existing device if any
      if (deviceRef.current) {
        deviceRef.current.destroy();
        deviceRef.current = null;
      }

      setDeviceStatus('initializing');
      setErrorMessage('');

      // Get access token
      const { data: tokenResponse, error: tokenError } = await supabase.functions.invoke('twilio-access-token', {
        body: { business_id: selectedBusiness.id }
      });

      if (tokenError || !tokenResponse?.success) {
        throw new Error(`Failed to get access token: ${tokenError?.message || tokenResponse?.error}`);
      }

      const { access_token } = tokenResponse;

      // Create Twilio Device
      const device = new Device(access_token, {
        logLevel: 0, // Minimal logging
        codecPreferences: ['opus', 'pcmu'],
        allowIncomingWhileBusy: true
      });

      deviceRef.current = device;

      // Set up device event listeners
      device.on('registered', () => {
        console.log('CallContext: Device registered successfully');
        setDeviceStatus('ready');
        setErrorMessage('');
      });

      device.on('unregistered', () => {
        setDeviceStatus('error');
        setErrorMessage('Device disconnected');
      });

      device.on('error', (error) => {
        console.error('Device error:', error);
        setDeviceStatus('error');
        setErrorMessage(`Device error: ${error.message}`);
      });

      device.on('destroy', () => {
        setDeviceStatus('error');
        setErrorMessage('Device destroyed');
      });

      // Call event listeners
      device.on('connect', (call) => {
        console.log('CallContext: Call connected');
        setActiveCallState(prev => ({
          ...prev,
          call,
          callStatus: 'connected',
          callStartTime: new Date()
        }));
      });

      device.on('disconnect', (call) => {
        console.log('CallContext: Call disconnected');
        setActiveCallState({
          call: null,
          phoneNumber: '',
          customerInfo: null,
          callStatus: 'idle',
          isMuted: false,
          isSpeakerOn: false,
          callStartTime: null,
          isMinimized: false
        });
      });

      device.on('incoming', (call) => {
        console.log('CallContext: Incoming call');
        setActiveCallState(prev => ({
          ...prev,
          call,
          callStatus: 'ringing'
        }));
        
        // Set up incoming call event listeners
        call.on('accept', () => {
          setActiveCallState(prev => ({
            ...prev,
            callStatus: 'connected',
            callStartTime: new Date()
          }));
        });
        
        call.on('disconnect', () => {
          setActiveCallState({
            call: null,
            phoneNumber: '',
            customerInfo: null,
            callStatus: 'idle',
            isMuted: false,
            isSpeakerOn: false,
            callStartTime: null,
            isMinimized: false
          });
        });
        
        // Auto-accept incoming calls (or you can make this configurable)
        call.accept();
      });

      // Register the device
      device.register();

    } catch (error: any) {
      console.error('CallContext initialization error:', error);
      setDeviceStatus('error');
      setErrorMessage(error?.message || 'Initialization failed');
    }
  };

  // Initialize device when business is selected
  useEffect(() => {
    if (selectedBusiness?.id) {
      initializeDevice();
    }

    return () => {
      if (deviceRef.current) {
        deviceRef.current.destroy();
        deviceRef.current = null;
      }
    };
  }, [selectedBusiness?.id]);

  // Start a call
  const startCall = async (
    phoneNumber: string,
    customerInfo?: { id?: string; first_name?: string; last_name?: string },
  ) => {
    if (!phoneNumber) {
      setErrorMessage('No number entered');
      return;
    }
    if (!isNativeIos && (!deviceRef.current || deviceStatus !== 'ready')) {
      setErrorMessage('Device not ready or no number entered');
      return;
    }
    if (isNativeIos && deviceStatus !== 'ready') {
      setErrorMessage('Device is not ready. Please wait for initialization.');
      return;
    }

    const formattedNumber = formatPhoneNumberForCalling(phoneNumber);

    // Native iOS: use Twilio Voice iOS SDK via Capacitor plugin
    if (isNativeIos) {
      try {
        setActiveCallState(prev => ({
          ...prev,
          callStatus: 'connecting',
          phoneNumber: formattedNumber,
          customerInfo: customerInfo || null,
          isMinimized: false
        }));
        setErrorMessage('');
        const { data: tokenResponse, error: tokenError } = await supabase.functions.invoke('twilio-access-token', {
          body: { business_id: selectedBusiness!.id }
        });
        if (tokenError || !tokenResponse?.success || !tokenResponse?.access_token) {
          const msg = tokenError?.message || tokenResponse?.error || 'Failed to get access token';
          setActiveCallState(prev => ({ ...prev, callStatus: 'idle' }));
          setErrorMessage(msg);
          throw new Error(msg);
        }
        const result = await CapacitorTwilioVoice.startCall({
          accessToken: tokenResponse.access_token,
          to: formattedNumber,
          businessId: selectedBusiness!.id
        });
        if (!result.success) {
          setActiveCallState(prev => ({ ...prev, callStatus: 'idle' }));
          setErrorMessage('Failed to start call');
          throw new Error('Failed to start call');
        }
        // Same path as SMS: log CRM activity + clear unread for open leads
        void recordPhoneCallOpportunityActivity({
          businessId: selectedBusiness!.id,
          phoneNumber: formattedNumber,
          customerId: customerInfo?.id,
          direction: 'outbound',
          callStatus: 'ringing',
        });
        isNativeIosCallRef.current = true;
        nativeCallListenerRef.current = await CapacitorTwilioVoice.addListener('callStateChange', (event) => {
          if (event.state === 'connected') {
            setActiveCallState(prev => ({
              ...prev,
              callStatus: 'connected',
              callStartTime: new Date(),
              isSpeakerOn: event.isSpeakerOn ?? false,
              isMuted: event.isMuted ?? false,
            }));
            setErrorMessage('');
          } else if (event.state === 'failed') {
            isNativeIosCallRef.current = false;
            nativeCallListenerRef.current?.remove().then(() => { nativeCallListenerRef.current = null; });
            setActiveCallState({
              call: null,
              phoneNumber: '',
              customerInfo: null,
              callStatus: 'idle',
              isMuted: false,
              isSpeakerOn: false,
              callStartTime: null,
              isMinimized: false
            });
            if (event.error) setErrorMessage(event.error);
          } else if (event.state === 'disconnected') {
            isNativeIosCallRef.current = false;
            nativeCallListenerRef.current?.remove().then(() => { nativeCallListenerRef.current = null; });
            setActiveCallState({
              call: null,
              phoneNumber: '',
              customerInfo: null,
              callStatus: 'idle',
              isMuted: false,
              isSpeakerOn: false,
              callStartTime: null,
              isMinimized: false
            });
            if (event.error) setErrorMessage(event.error);
          } else if (event.state === 'ringing') {
            setActiveCallState(prev => ({ ...prev, callStatus: 'ringing' }));
          }
        });
      } catch (err: any) {
        setActiveCallState(prev => ({ ...prev, callStatus: 'idle' }));
        setErrorMessage(err?.message || 'Failed to start call');
        throw err; // So dialler dropdown stays open and shows error
      }
      return;
    }

    try {
      // Request microphone access first (web only)
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (mediaError: any) {
        setErrorMessage(`Microphone access denied: ${mediaError.message}`);
        return;
      }

      // Double-check device is still available
      if (!deviceRef.current) {
        setErrorMessage('Device connection lost. Please try again.');
        return;
      }

      setActiveCallState(prev => ({
        ...prev,
        callStatus: 'connecting',
        phoneNumber: formattedNumber,
        customerInfo: customerInfo || null,
        isMinimized: false
      }));
      setErrorMessage('');

      const sender = await resolveCurrentSender(selectedBusiness!.id);
      const call = await deviceRef.current.connect({
        params: {
          To: formattedNumber,
          business_id: selectedBusiness!.id,
          ...(customerInfo?.id ? { customer_id: customerInfo.id } : {}),
          sender_kind: sender.kind,
          ...(sender.id ? { sender_id: sender.id } : {}),
          ...(sender.name ? { sender_name: sender.name } : {}),
        }
      });

      // Same path as SMS: log CRM activity + clear unread for open leads
      void recordPhoneCallOpportunityActivity({
        businessId: selectedBusiness!.id,
        phoneNumber: formattedNumber,
        customerId: customerInfo?.id,
        direction: 'outbound',
        callStatus: 'ringing',
      });

      const recordOutboundInMessages = (sid?: string | null) => {
        if (!sid || !selectedBusiness?.id) return;
        void ensureOutboundCallHistory({
          businessId: selectedBusiness.id,
          callSid: sid,
          toNumber: formattedNumber,
          fromNumber: (selectedBusiness as any)?.VITE_TWILIO_PHONE_NUMBER || null,
          customerId: customerInfo?.id,
          status: 'ringing',
          sender,
        });
      };

      // CallSid is often available immediately; also catch ringing/accept.
      recordOutboundInMessages((call as any)?.parameters?.CallSid || (call as any)?.outboundConnectionId);
      call.on('ringing', () => {
        recordOutboundInMessages((call as any)?.parameters?.CallSid || (call as any)?.outboundConnectionId);
      });
      
      setActiveCallState(prev => ({
        ...prev,
        call,
        callStatus: 'connecting'
      }));
      
      // Set up call-specific event listeners
      call.on('accept', () => {
        recordOutboundInMessages((call as any)?.parameters?.CallSid || (call as any)?.outboundConnectionId);
        setActiveCallState(prev => ({
          ...prev,
          callStatus: 'connected',
          callStartTime: new Date()
        }));
        setErrorMessage('');
      });
      
      call.on('disconnect', () => {
        // Clean up custom audio element if we created one
        const customAudio = (call as any)._customAudioElement as HTMLAudioElement;
        if (customAudio) {
          customAudio.pause();
          customAudio.srcObject = null;
          if (customAudio.parentNode) {
            customAudio.parentNode.removeChild(customAudio);
          }
          delete (call as any)._customAudioElement;
        }
        
        setActiveCallState({
          call: null,
          phoneNumber: '',
          customerInfo: null,
          callStatus: 'idle',
          isMuted: false,
          isSpeakerOn: false,
          callStartTime: null,
          isMinimized: false
        });
        setErrorMessage('');
      });
      
      call.on('reject', () => {
        setActiveCallState(prev => ({
          ...prev,
          call: null,
          callStatus: 'idle'
        }));
        setErrorMessage('Call was rejected');
      });
      
      call.on('cancel', () => {
        setActiveCallState(prev => ({
          ...prev,
          call: null,
          callStatus: 'idle'
        }));
        setErrorMessage('Call was cancelled');
      });
      
      call.on('error', (error: any) => {
        setActiveCallState(prev => ({
          ...prev,
          call: null,
          callStatus: 'idle'
        }));
        setErrorMessage(`Call error: ${error.message || 'Unknown error'}`);
      });
      
    } catch (error: any) {
      console.error('Start call error:', error);
      setActiveCallState(prev => ({
        ...prev,
        call: null,
        callStatus: 'idle'
      }));
      const msg = `Call failed: ${error.message || 'Unknown error'}`;
      setErrorMessage(msg);
      throw error; // So dialler dropdown stays open and shows error
    }
  };

  // End call
  const endCall = () => {
    if (isNativeIosCallRef.current) {
      isNativeIosCallRef.current = false;
      nativeCallListenerRef.current?.remove().then(() => { nativeCallListenerRef.current = null; });
      CapacitorTwilioVoice.endCall();
      setActiveCallState({
        call: null,
        phoneNumber: '',
        customerInfo: null,
        callStatus: 'idle',
        isMuted: false,
        isSpeakerOn: false,
        callStartTime: null,
        isMinimized: false
      });
      return;
    }
    if (activeCallState.call) {
      // Clean up any custom audio elements and unmute Twilio's audio before disconnecting (web)
      const call = activeCallState.call as any;
      const customAudio = call._customAudioElement as HTMLAudioElement;
      if (customAudio) {
        customAudio.pause();
        customAudio.srcObject = null;
        if (customAudio.parentNode) {
          customAudio.parentNode.removeChild(customAudio);
        }
        delete call._customAudioElement;
      }
      // Ensure all audio elements are unmuted before disconnect (fix for previous speaker toggle bug)
      const allAudioElements = Array.from(document.querySelectorAll('audio')) as HTMLAudioElement[];
      for (const el of allAudioElements) {
        if (el.muted && el.srcObject instanceof MediaStream) {
          el.muted = false;
        }
      }
      activeCallState.call.disconnect();
    }
    setActiveCallState({
      call: null,
      phoneNumber: '',
      customerInfo: null,
      callStatus: 'idle',
      isMuted: false,
      isSpeakerOn: false,
      callStartTime: null,
      isMinimized: false
    });
  };

  // Toggle mute
  const toggleMute = () => {
    const newMuteState = !activeCallState.isMuted;

    // Native iOS: route mute through Capacitor plugin
    if (isNativeIosCallRef.current) {
      CapacitorTwilioVoice.toggleMute({ enabled: newMuteState }).catch(err => {
        console.warn('Native toggleMute failed:', err);
      });
      setActiveCallState(prev => ({ ...prev, isMuted: newMuteState }));
      return;
    }

    // Web: use Twilio JS SDK call.mute()
    if (activeCallState.call) {
      activeCallState.call.mute(newMuteState);
      setActiveCallState(prev => ({
        ...prev,
        isMuted: newMuteState
      }));
    }
  };

  // Toggle speaker
  const toggleSpeaker = async () => {
    const newSpeakerState = !activeCallState.isSpeakerOn;
    
    try {
      // Native iOS: use Capacitor plugin for speaker toggle (earpiece/speaker + proximity sensor)
      if (isNativeIosCallRef.current) {
        try {
          await CapacitorTwilioVoice.toggleSpeaker({ enabled: newSpeakerState });
          console.log(`Native iOS speaker toggle: ${newSpeakerState ? 'SPEAKER' : 'EARPIECE'}`);
        } catch (nativeErr) {
          console.warn('Native speaker toggle failed:', nativeErr);
        }
        setActiveCallState(prev => ({ ...prev, isSpeakerOn: newSpeakerState }));
        return;
      }

      // Web calls: use Twilio Device audio helpers (preferred) or setSinkId fallback
      if (!activeCallState.call) {
        return;
      }

      const call = activeCallState.call as any;
      const device = deviceRef.current as any;
      let speakerSet = false;

      // Method 1: Use Twilio Device.audio.speakerDevices if available (Twilio JS SDK 2.x)
      if (device?.audio?.speakerDevices) {
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const audioOutputs = devices.filter(d => d.kind === 'audiooutput');
          console.log('🔊 Available audio outputs:', audioOutputs.map(d => ({ id: d.deviceId, label: d.label })));
          
          if (newSpeakerState) {
            // Find a speaker device
            const speakerDevice = audioOutputs.find(d => 
              d.label.toLowerCase().includes('speaker')
            ) || audioOutputs.find(d => d.deviceId === 'default');
            
            if (speakerDevice) {
              device.audio.speakerDevices.set(speakerDevice.deviceId);
              console.log('✅ Speaker enabled via Twilio Device.audio:', speakerDevice.label || speakerDevice.deviceId);
              speakerSet = true;
            }
          } else {
            // Reset to default / communications device
            const commDevice = audioOutputs.find(d => 
              d.deviceId === 'communications' ||
              d.label.toLowerCase().includes('earpiece') ||
              d.label.toLowerCase().includes('headphone')
            ) || audioOutputs.find(d => d.deviceId === 'default');
            
            if (commDevice) {
              device.audio.speakerDevices.set(commDevice.deviceId);
              console.log('✅ Earpiece enabled via Twilio Device.audio:', commDevice.label || commDevice.deviceId);
              speakerSet = true;
            }
          }
        } catch (audioErr) {
          console.warn('⚠️ Twilio Device.audio approach failed:', audioErr);
        }
      }

      // Method 2: Find Twilio's audio element and use setSinkId directly
      // IMPORTANT: Never create a new audio element or mute Twilio's - just control the existing one
      if (!speakerSet) {
        let audioElement: HTMLAudioElement | null = null;
        
        // Try to get from remote stream
        if (call.getRemoteStream) {
          const remoteStream = call.getRemoteStream();
          if (remoteStream) {
            const allAudioElements = Array.from(document.querySelectorAll('audio')) as HTMLAudioElement[];
            for (const el of allAudioElements) {
              if (el.srcObject === remoteStream) {
                audioElement = el;
                break;
              }
            }
          }
        }
        
        // Fallback: find any audio element with a MediaStream
        if (!audioElement) {
          const allAudioElements = Array.from(document.querySelectorAll('audio')) as HTMLAudioElement[];
          for (const el of allAudioElements) {
            if (el.srcObject instanceof MediaStream) {
              const stream = el.srcObject as MediaStream;
              if (stream.getAudioTracks().length > 0) {
                audioElement = el;
                break;
              }
            }
          }
        }
        
        if (audioElement) {
          // Ensure the audio element is not muted and is playing
          if (audioElement.muted) {
            audioElement.muted = false;
            console.log('🔊 Unmuted audio element that was incorrectly muted');
          }
          if (audioElement.paused) {
            try {
              await audioElement.play();
              console.log('🔊 Resumed paused audio element');
            } catch (playErr) {
              console.warn('⚠️ Could not resume audio:', playErr);
            }
          }
          
          // Try setSinkId if supported (Chrome, Edge - NOT Safari/iOS)
          if ('setSinkId' in audioElement) {
            try {
              const devices = await navigator.mediaDevices.enumerateDevices();
              const audioOutputs = devices.filter(d => d.kind === 'audiooutput');
              
              if (newSpeakerState) {
                const speakerDevice = audioOutputs.find(d => 
                  d.label.toLowerCase().includes('speaker')
                ) || audioOutputs.find(d => d.deviceId === 'default');
                
                if (speakerDevice) {
                  await (audioElement as any).setSinkId(speakerDevice.deviceId);
                  console.log('✅ Speaker enabled via setSinkId:', speakerDevice.label || speakerDevice.deviceId);
                  speakerSet = true;
                }
              } else {
                const commDevice = audioOutputs.find(d => 
                  d.deviceId === 'communications' ||
                  d.label.toLowerCase().includes('earpiece') ||
                  d.label.toLowerCase().includes('headphone')
                ) || audioOutputs.find(d => d.deviceId !== 'default');
                
                if (commDevice) {
                  await (audioElement as any).setSinkId(commDevice.deviceId);
                  console.log('✅ Earpiece enabled via setSinkId:', commDevice.label || commDevice.deviceId);
                  speakerSet = true;
                } else {
                  await (audioElement as any).setSinkId('');
                  console.log('✅ Reset to default audio output');
                  speakerSet = true;
                }
              }
            } catch (sinkError) {
              console.warn('⚠️ setSinkId not supported or failed:', sinkError);
            }
          }
          
          // Method 3: Volume boost as visual/audio feedback when setSinkId unavailable (iOS/Safari)
          if (!speakerSet) {
            // On iOS/Safari, setSinkId is not available. Audio plays through the OS-controlled output.
            // We can at least ensure volume is at max.
            audioElement.volume = 1.0;
            console.log('🔊 setSinkId not available (iOS/Safari) - volume set to max. Audio routing controlled by OS.');
            speakerSet = true;
          }
        } else {
          console.warn('⚠️ No audio element found for speaker control');
        }
      }
      
      // Update state
      setActiveCallState(prev => ({
        ...prev,
        isSpeakerOn: newSpeakerState
      }));
      
      console.log(`🔊 Speaker toggle: ${newSpeakerState ? 'ON' : 'OFF'} (applied: ${speakerSet})`);
    } catch (error) {
      console.error('Error toggling speaker:', error);
      // Still update state for UI consistency
      setActiveCallState(prev => ({
        ...prev,
        isSpeakerOn: newSpeakerState
      }));
    }
  };

  // Send DTMF digits during call
  const sendDTMF = (digit: string) => {
    if (activeCallState.callStatus !== 'connected') return;

    // Native iOS: route DTMF through Capacitor plugin
    if (isNativeIosCallRef.current) {
      CapacitorTwilioVoice.sendDTMF({ digits: digit }).catch(err => {
        console.error('Native sendDTMF failed:', err);
        setErrorMessage('Failed to send keypad input');
      });
      return;
    }

    // Web: use Twilio JS SDK call.sendDigits()
    if (activeCallState.call) {
      try {
        activeCallState.call.sendDigits(digit);
        console.log('Sent DTMF digit:', digit);
      } catch (error) {
        console.error('Error sending DTMF:', error);
        setErrorMessage('Failed to send keypad input');
      }
    }
  };

  // Minimize call
  const minimizeCall = () => {
    setActiveCallState(prev => ({
      ...prev,
      isMinimized: true
    }));
  };

  // Maximize call
  const maximizeCall = () => {
    setActiveCallState(prev => ({
      ...prev,
      isMinimized: false
    }));
  };

  // Function to load recent call history
  useEffect(() => {
    if (!supabaseClient || !selectedBusiness?.id) return;

    const loadCallHistory = async () => {
      try {
        const { data, error } = await supabaseClient
          .from('twilio_calls')
          .select('*')
          .eq('business_id', selectedBusiness.id)
          .order('created_at', { ascending: false })
          .limit(10);

        if (error) {
          console.error('Error loading call history:', error);
          return;
        }

        if (data) {
          const formattedCalls = data.map((call: any) => ({
            id: call.id,
            from: call.from_number,
            to: call.to_number,
            status: call.status,
            direction: call.direction,
            businessId: call.business_id,
            createdAt: call.created_at,
            communication_id: call.communication_id,
            first_name: call.first_name,
            last_name: call.last_name,
            customer_phone: call.customer_phone,
            customer_email: call.customer_email
          }));
          
          setCallHistory(formattedCalls);
        }
      } catch (error) {
        console.error('Failed to load call history:', error);
      }
    };

    loadCallHistory();
  }, [supabaseClient, selectedBusiness?.id]);

  // Subscribe to call changes (for incoming calls from database)
  useEffect(() => {
    if (!supabaseClient || !selectedBusiness?.id) return;

    console.log('Setting up call subscription for business ID:', selectedBusiness.id);

    // Subscribe to new calls
    const subscription = supabaseClient
      .channel('twilio_calls_channel')
      .on('postgres_changes', 
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'twilio_calls',
          filter: `business_id=eq.${selectedBusiness.id}`
        }, 
        (payload: any) => {
          console.log('New call received via realtime:', payload);
          const newCall: CallInfo = {
            id: payload.new.id,
            from: payload.new.from_number,
            to: payload.new.to_number,
            status: payload.new.status,
            direction: payload.new.direction,
            businessId: payload.new.business_id,
            createdAt: payload.new.created_at,
            communication_id: payload.new.communication_id,
            first_name: payload.new.first_name,
            last_name: payload.new.last_name,
            customer_phone: payload.new.customer_phone,
            customer_email: payload.new.customer_email
          };

          // Add to call history
          setCallHistory(prev => [newCall, ...prev].slice(0, 10));

          // Set as current call if it's ringing and inbound
          if (newCall.status === 'ringing' && newCall.direction === 'inbound') {
            console.log('Showing notification for incoming call from:', newCall.from);
            
            // Get the latest call data with customer info before showing notification
            fetchCallWithCustomerInfo(newCall.id).then(updatedCall => {
              // If we got updated data with customer info, use it
              if (updatedCall) {
                setCurrentCall(updatedCall);
                showIncomingCallNotification(updatedCall);
              } else {
                // Otherwise use what we have
                setCurrentCall(newCall);
                showIncomingCallNotification(newCall);
              }
            }).catch(err => {
              // On error, fallback to using the original call data
              console.error('Error fetching call with customer info:', err);
              setCurrentCall(newCall);
              showIncomingCallNotification(newCall);
            });
          }
        }
      )
      .on('postgres_changes', 
        { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'twilio_calls',
          filter: `business_id=eq.${selectedBusiness.id}`
        }, 
        (payload: any) => {
          console.log('Call update received via realtime:', payload);
          const updatedCall: CallInfo = {
            id: payload.new.id,
            from: payload.new.from_number,
            to: payload.new.to_number,
            status: payload.new.status,
            direction: payload.new.direction,
            businessId: payload.new.business_id,
            createdAt: payload.new.created_at,
            communication_id: payload.new.communication_id,
            first_name: payload.new.first_name,
            last_name: payload.new.last_name,
            customer_phone: payload.new.customer_phone,
            customer_email: payload.new.customer_email
          };

          // Update in call history
          setCallHistory(prev => 
            prev.map(call => call.id === updatedCall.id ? updatedCall : call)
          );

          // Update current call if it's the same ID
          if (currentCall?.id === updatedCall.id) {
            if (['completed', 'failed', 'busy', 'no-answer', 'canceled'].includes(updatedCall.status)) {
              console.log(`Call ${updatedCall.id} status changed to ${updatedCall.status}, clearing notification`);
              // Stop ringtone
              stopRingtone();
              // Clear current call state
              setCurrentCall(null);
              // Dismiss the notification
              dismissCallNotification(updatedCall.id);
            } else {
              setCurrentCall(updatedCall);
            }
          }
        }
      )
      .subscribe((status) => {
        console.log('Subscription status:', status);
      });

    return () => {
      console.log('Cleaning up call subscription');
      supabaseClient.removeChannel(subscription);
    };
  }, [supabaseClient, selectedBusiness?.id, currentCall]);

  // Show a toast notification for incoming calls
  const showIncomingCallNotification = (call: CallInfo) => {
    console.log('Attempting to show notification for call:', call);
    // Debug - log the full call details to check if customer info is present
    console.log('Call details for notification:', {
      id: call.id,
      from: call.from,
      communication_id: call.communication_id,
      first_name: call.first_name,
      last_name: call.last_name
    });

    // Format phone number for display
    const formattedPhoneNumber = formatPhoneNumberForDisplay(call.from);
    
    // Determine caller display name with additional logging
    const hasCustomerInfo = Boolean(call.communication_id && call.first_name && call.last_name);
    console.log(`Call has customer info: ${hasCustomerInfo}`);
    
    const callerDisplay = hasCustomerInfo
      ? `${call.first_name} ${call.last_name}`
      : formattedPhoneNumber;
    
    console.log(`Will display caller as: "${callerDisplay}"`);
    
    toast.custom(
      (t) => (
        <div
          className={`${
            t.visible ? 'animate-enter' : 'animate-leave'
          } max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex flex-col ring-1 ring-black ring-opacity-5 fixed top-4 left-4 z-[9999]`}
        >
          <div className="p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-900">
                  Incoming Call
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  From: {callerDisplay}
                </p>
              </div>
              <div className="ml-4 flex-shrink-0 flex">
                <button
                  onClick={() => {
                    answerCall();
                    toast.dismiss(t.id);
                  }}
                  className="bg-green-100 hover:bg-green-200 text-green-800 px-3 py-1 rounded-full mr-2"
                >
                  Answer
                </button>
                <button
                  onClick={() => {
                    rejectCall();
                    toast.dismiss(t.id);
                  }}
                  className="bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded-full"
                >
                  Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      ),
      { 
        duration: 30000, // Show for 30 seconds or until dismissed
        id: 'incoming-call-' + call.id, // Use a unique ID based on the call ID
      }
    );

    // Also play a ringtone
    playRingtone();
  };

  // Play a ringtone sound
  const playRingtone = () => {
    try {
      const audio = new Audio('/sounds/ringtone.mp3');
      audio.loop = true;
      audio.play().catch(e => console.error('Could not play ringtone:', e));
      
      // Store the audio element to stop it later
      (window as any).ringtoneAudio = audio;

      // Stop after 30 seconds maximum
      setTimeout(() => {
        if ((window as any).ringtoneAudio) {
          (window as any).ringtoneAudio.pause();
          (window as any).ringtoneAudio = null;
        }
      }, 30000);
    } catch (error) {
      console.error('Error playing ringtone:', error);
    }
  };

  // Stop the ringtone
  const stopRingtone = () => {
    if ((window as any).ringtoneAudio) {
      (window as any).ringtoneAudio.pause();
      (window as any).ringtoneAudio = null;
    }
  };

  // Answer the current call
  const answerCall = async () => {
    if (!currentCall) return;
    
    stopRingtone();
    
    try {
      // Here you would typically implement call answering logic
      // For web-based implementation, you'd need to utilize Twilio Voice JS SDK
      // For now, we'll just update the status in our database
      
      if (supabaseClient) {
        await supabaseClient
          .from('twilio_calls')
          .update({ status: 'in-progress' })
          .eq('id', currentCall.id);
      }
      
      toast.success('Call answered');
    } catch (error) {
      console.error('Error answering call:', error);
      toast.error('Failed to answer call');
    }
  };

  // Reject the current call
  const rejectCall = async () => {
    if (!currentCall) return;
    
    stopRingtone();
    
    try {
      // Here you would typically implement call rejection logic
      // For now, we'll just update the status in our database
      
      if (supabaseClient) {
        await supabaseClient
          .from('twilio_calls')
          .update({ status: 'canceled' })
          .eq('id', currentCall.id);
      }
      
      setCurrentCall(null);
      toast.success('Call rejected');
    } catch (error) {
      console.error('Error rejecting call:', error);
      toast.error('Failed to reject call');
    }
  };

  // Clear the current call manually
  const clearCurrentCall = () => {
    stopRingtone();
    setCurrentCall(null);
  };

  // Function to get the latest call data with customer info
  const fetchCallWithCustomerInfo = async (callId: string): Promise<CallInfo | null> => {
    if (!supabaseClient) return null;
    
    try {
      // Get the full call information including customer data
      const { data, error } = await supabaseClient
        .from('twilio_calls')
        .select('*')
        .eq('id', callId)
        .maybeSingle();
        
      if (error) {
        console.error('Error fetching call with customer info:', error);
        return null;
      }
      
      if (!data) return null;
      
      console.log('Fetched call with customer info:', data);
      
      // Format to our CallInfo structure
      return {
        id: data.id,
        from: data.from_number,
        to: data.to_number,
        status: data.status,
        direction: data.direction,
        businessId: data.business_id,
        createdAt: data.created_at,
        communication_id: data.communication_id,
        first_name: data.first_name,
        last_name: data.last_name,
        customer_phone: data.customer_phone,
        customer_email: data.customer_email
      };
    } catch (err) {
      console.error('Error in fetchCallWithCustomerInfo:', err);
      return null;
    }
  };

  // Function to dismiss a call notification by ID
  const dismissCallNotification = (callId: string) => {
    // Dismiss the toast notification
    toast.dismiss(`incoming-call-${callId}`);
  };

  // End AI voice mode the moment ANY phone call becomes active — user-dialed
  // from the dialler, AI-dialed via call_person, or an incoming call being
  // accepted. The mic belongs to the phone call; VoiceHelpContext listens for
  // this event and closes its session.
  const prevCallStatusRef = useRef(activeCallState.callStatus);
  useEffect(() => {
    const prev = prevCallStatusRef.current;
    prevCallStatusRef.current = activeCallState.callStatus;
    if (prev === 'idle' && activeCallState.callStatus !== 'idle') {
      window.dispatchEvent(new CustomEvent('phone:call-active'));
    }
  }, [activeCallState.callStatus]);

  // ── AI assistant bridge ────────────────────────────────────────────────
  // A persistent "phone" surface so the assistant's call_person tool can
  // actually place calls through the same path as the dialler. Live ref so
  // the handler always reads current state (registered once on mount).
  const aiPhoneBridgeRef = useRef({ deviceStatus, activeCallState, startCall, initializeDevice });
  aiPhoneBridgeRef.current = { deviceStatus, activeCallState, startCall, initializeDevice };

  useEffect(() => {
    return registerAssistantSurface('phone', {
      call: async (payload) => {
        const bridge = aiPhoneBridgeRef.current;
        const phone = String(payload.phone || '').trim();
        const displayName = String(payload.displayName || '').trim();
        if (!phone) return 'error: no phone number to dial.';
        if (bridge.activeCallState.callStatus !== 'idle') {
          const current = bridge.activeCallState.customerInfo?.first_name || bridge.activeCallState.phoneNumber;
          return `error: a call with ${current || 'someone'} is already ${bridge.activeCallState.callStatus} — it must end before another call can start.`;
        }
        if (bridge.deviceStatus === 'not_configured') {
          return 'error: calling is not set up for this business (no phone service configured in Twilio settings) — cannot place calls.';
        }
        if (bridge.deviceStatus !== 'ready') {
          // The JS device drifts to 'error' when its hour-long Twilio token
          // lapses; the dialler UI recovers by re-initializing on open — do
          // the same here instead of refusing the call.
          if (bridge.deviceStatus === 'error') {
            bridge.initializeDevice().catch(() => { /* status stays non-ready; handled below */ });
          }
          const deadline = Date.now() + 8000;
          while (Date.now() < deadline && aiPhoneBridgeRef.current.deviceStatus !== 'ready') {
            if (aiPhoneBridgeRef.current.deviceStatus === 'not_configured') {
              return 'error: calling is not set up for this business (no phone service configured in Twilio settings) — cannot place calls.';
            }
            await new Promise((r) => setTimeout(r, 250));
          }
          if (aiPhoneBridgeRef.current.deviceStatus !== 'ready') {
            return 'error: the phone system could not connect (it may need the Twilio settings checked) — the call was not placed.';
          }
        }
        try {
          const customerInfo = payload.customerId || displayName
            ? {
                id: (payload.customerId as string) || undefined,
                first_name: displayName.split(/\s+/)[0] || undefined,
                last_name: displayName.split(/\s+/).slice(1).join(' ') || undefined,
              }
            : undefined;
          await bridge.startCall(phone, customerInfo);
          return `Dialing ${displayName ? `${displayName} at ` : ''}${phone} — the call screen is up and ringing. Tell the user in ONE short sentence you're calling, then go quiet; they are about to be on the phone.`;
        } catch (err) {
          return `error: the call could not be started — ${err instanceof Error ? err.message : 'unknown error'}.`;
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Value to be provided by the context
  const value: CallContextType = {
    // Legacy
    currentCall,
    callHistory,
    answerCall,
    rejectCall,
    clearCurrentCall,
    setCurrentCall,
    
    // New active call state
    activeCallState,
    device: deviceRef.current,
    deviceStatus,
    errorMessage,
    
    // Methods
    startCall,
    endCall,
    toggleMute,
    toggleSpeaker,
    sendDTMF,
    minimizeCall,
    maximizeCall,
    initializeDevice
  };

  return (
    <CallContext.Provider value={value}>
      {children}
    </CallContext.Provider>
  );
}

// Hook to use the Call context
export function useCall() {
  const context = useContext(CallContext);
  
  if (context === undefined) {
    throw new Error('useCall must be used within a CallProvider');
  }
  
  return context;
}
